/**
 * AI-OS Insight Panel — THE Contextual-Brain component (frozen י.7 + י״א.11).
 *
 * A COMPONENT, not a screen (Layer Law ט.1): it renders the one Brain's
 * context for the screen it sits on; only the context filter changes.
 *
 * Permission model by construction: the panel reads through /ai/brain/*
 * only, and HIDES ITSELF entirely on 401/403 — the backend permission seam
 * stays the single authority; no key logic is duplicated in the frontend.
 *
 * Honesty: real seam data or an honest empty state (rendered by the caller
 * through `render`); zero writes — any suggestion shown is an ephemeral
 * projection (candidate-write rule ח.5-7). Hidden on native (Apple-2.1
 * gating precedent — same rule as the /ai-os route).
 *
 * Tokens: existing screens don't carry the --aios-* variables, so the panel
 * root applies aiosCssVars itself.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import { aiosCssVars } from "../tokens";
import { AiosCard, LoadPanel } from "./core";
import isNativeApp from "../../utils/isNativeApp";

const InsightPanel = ({ title, subtitle, footnote, fetcher, render }) => {
  const theme = useTheme();
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
    forbidden: false,
  });

  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
  }, []);

  const load = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetcher();
      if (mountedRef.current) {
        setState({ loading: false, error: null, data, forbidden: false });
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err && err.response && err.response.status;
      if (status === 401 || status === 403) {
        // No permission → the panel simply doesn't exist for this user.
        setState({ loading: false, error: null, data: null, forbidden: true });
      } else {
        setState({ loading: false, error: err, data: null, forbidden: false });
      }
    }
  }, [fetcher]);

  // Native never fetches (Ultracode fix): effects run even for null-rendering
  // components, so the guard must sit INSIDE the effect, not only at render.
  useEffect(() => {
    if (!isNativeApp()) load();
  }, [load]);

  if (isNativeApp()) return null;
  if (state.forbidden) return null;

  return (
    <Box
      style={{
        ...aiosCssVars(theme.palette.type),
        marginBlockEnd: 16,
      }}
    >
      <AiosCard title={title} subtitle={subtitle} footnote={footnote}>
        <LoadPanel loading={state.loading} error={state.error} onRetry={load}>
          {state.data ? (
            // Height-bounded content (Ultracode fix): the panel is a GUEST on
            // existing screens whose layouts are fixed-height flex columns —
            // it must never squeeze the host screen's primary content out.
            <Box style={{ maxBlockSize: 420, overflow: "auto" }}>
              {render(state.data)}
            </Box>
          ) : null}
        </LoadPanel>
      </AiosCard>
    </Box>
  );
};

export default InsightPanel;
